import React, { useRef, useState } from "react";

import { useTranslation } from 'react-i18next';

import useTerminology from "../../../../../hooks/useTerminology";

import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { CircularProgress } from "@material-ui/core";

import { useStyles } from "./css";
import { UserServiceCertification } from "../../../../../models/user-service-certification";
import { DeleteIcon, PlusIcon } from "../../../../../assets/icons";
import ConfirmDeleteModal from "../../../../common/ConfirmDeleteModal";
import { UserServiceCertificationsProps } from "./props";
import { TerminologyType } from "../../../../../models/enums/terminology-type";
import { TerminologyForm } from "../../../../../models/enums/terminology-form";


export default function UserServiceCertifications(props: UserServiceCertificationsProps) {

  const { t } = useTranslation(["general"]);
  const classes = useStyles();

  const { certifications, setCertifications, handleS3ImageSave } = props;

  const certTerminology = useTerminology({ type: TerminologyType.Certification, form: TerminologyForm.Singular });

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [certToDelete, setCertToDelete] = useState<UserServiceCertification | null>(null);

  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);

  const fileInputTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleCertificationUpload(file: File) {
    const dataImg = await handleS3ImageSave(file);

    if (dataImg) {

      const newOrder = certifications.certifications.length > 0
        ? Math.max(...certifications.certifications.map(cert => cert.order)) + 1
        : 0;

      const newImg: UserServiceCertification = {
        imageS3Key: dataImg.fileName,
        fileName: file.name,
        order: newOrder,
        serviceId: certifications.serviceId,
        userId: certifications.userId || null
      }

      const updatedCerts = [...certifications.certifications, newImg];
      setCertifications({ ...certifications, certifications: updatedCerts });
    }
  }

  async function handleFileInputChange(e: React.FormEvent<HTMLInputElement>) {
    const files = fileInputRef.current?.files;
    const file = files ? files[0] : null;

    setIsFileLoading(true);

    if (file && fileInputTypes.includes(file.type)) {
      await handleCertificationUpload(file);
    }

    setIsFileLoading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function showOpenFileDialog() {
    if (fileInputRef.current) {
      fileInputRef.current?.click();
    }
  }

  function confirmDelete(cert: UserServiceCertification) {
    setOpenDeleteDialog(true);
    setCertToDelete(cert);
  }

  async function handleDelete(cert: UserServiceCertification) {
    const updatedCerts = certifications.certifications.filter(c => c.order !== cert.order);

    setCertifications({
      serviceId: cert.serviceId,
      userId: cert.userId ?? null,
      certifications: updatedCerts
    });

    setOpenDeleteDialog(false);
  }

  return (
    <>
      <div className={classes.addCertificationsContainer}>
        <div className={classes.certificationsContainer}>
          {certifications.certifications.map((cert, index) => (
            <div
              key={index}
              className={cert.imageS3Key ? classes.certificationContainer : classes.certificationHiddenContainer}
            >
              <Typography className={classes.certificationName}>
                {cert.fileName}
              </Typography>
              <Button
                className={classes.deleteButton}
                onClick={() => confirmDelete(cert)}
              >
                <DeleteIcon />
              </Button>
            </div>
          ))}

          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            accept="image/jpeg, image/png"
            onChange={handleFileInputChange}
          />
          <Button
            className={classes.addButton}
            onClick={() => showOpenFileDialog()}
            disabled={isFileLoading}
          >
            <div className={classes.addButtonItems}>
              {isFileLoading
                ? (<CircularProgress style={{ width: 10, height: 10, color: 'initial' }} />)
                : <PlusIcon style={{ width: 10, height: 10 }} />
              }
              {t("Add certification", { terminology: certTerminology.toLowerCase() })}
            </div>
          </Button>
        </div>
        <div className={classes.certificationsNotesContainer}>
          <Typography className={classes.notesBoldText}>
            {`${t("Notes")}:`}
          </Typography>
          <Typography className={classes.notesText}>
            {t("This image will be displayed on the site for appointments used by customers. Allowed formats for the image: ")}
            <span className={classes.notesBoldText}>{t(".png or .jpg.")}</span>
            {t(" Maximum size: 800px x 800px.")}
          </Typography>
        </div>
      </div>

      <ConfirmDeleteModal
        classModal={classes.modalDelete}
        open={openDeleteDialog}
        item={certToDelete}
        componentInfo={
          <div className={`${classes.modalCenter} ${classes.modalTitleSpace}`}>
            <Typography className={classes.modalTitleText}>
              {t("Delete this image?")}
            </Typography>
          </div>
        }
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={handleDelete}
      />
    </>
  );
}
